import React from 'react';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';

export interface SectionNavItem {
  id: string;
  label: string;
}

// Accounts for the sticky navbar height plus a small gap. Used both as the
// sidebar's sticky offset and the scroll offset applied when navigating.
export const SECTION_NAV_OFFSET = 80;

interface SectionSidebarProps {
  title: string;
  sections: SectionNavItem[];
  activeId: string;
  onNavigate: (id: string) => void;
}

const SectionSidebar: React.FC<SectionSidebarProps> = ({
  title,
  sections,
  activeId,
  onNavigate
}) => {
  return (
    <>
      {/* Mobile and tablet: horizontal scrollable nav */}
      <Box display={{ base: 'block', lg: 'none' }}>
        <Box overflowX="auto" pb={1}>
          <Flex gap={0} minWidth="max-content" alignItems="stretch">
            {sections.map((section, index) => (
              <React.Fragment key={section.id}>
                <Flex
                  direction="column"
                  alignItems="center"
                  gap={1}
                  px={3}
                  py={2}
                  cursor="pointer"
                  borderBottom="3px solid"
                  borderColor={
                    section.id === activeId
                      ? 'actionSecondaryContent'
                      : 'transparent'
                  }
                  color={
                    section.id === activeId
                      ? 'actionSecondaryContent'
                      : undefined
                  }
                  onClick={() => onNavigate(section.id)}
                >
                  <Text
                    textStyle="textContext"
                    fontSize="xs"
                    whiteSpace="nowrap"
                    fontWeight={section.id === activeId ? 'bold' : undefined}
                  >
                    {section.label}
                  </Text>
                </Flex>
                {index < sections.length - 1 && (
                  <Flex alignItems="center">
                    <Box w="20px" h="1px" bgColor="neutralPrimaryBorder" />
                  </Flex>
                )}
              </React.Fragment>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* Desktop: vertical nav */}
      <VStack align="stretch" spacing={1} display={{ base: 'none', lg: 'flex' }}>
        <Text textStyle="headingItem" paddingX={3} paddingY={2}>
          {title}
        </Text>
        {sections.map(section => (
          <Box
            key={section.id}
            px={3}
            py={2}
            cursor="pointer"
            borderRadius="6px"
            borderLeft="3px solid"
            borderColor={
              section.id === activeId
                ? 'actionSecondaryContent'
                : 'transparent'
            }
            _hover={{ bg: 'surfaceSecondaryFill' }}
            onClick={() => onNavigate(section.id)}
          >
            <Text
              textStyle="textContext"
              fontWeight={section.id === activeId ? 'bold' : undefined}
              color={
                section.id === activeId ? 'actionSecondaryContent' : undefined
              }
            >
              {section.label}
            </Text>
          </Box>
        ))}
      </VStack>
    </>
  );
};

export default SectionSidebar;
